import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Upload,
} from "antd";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import config from "../../../config";
import authHeader from "../../Authentication/authHeader";
import Swal from "sweetalert2";
import TextArea from "antd/es/input/TextArea";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";

export default function ExpenseModal({
  visible,
  modalClose,
  data,
  isUpdate,
  userProfile,
  fetchExpenseList,
}) {
  const [form] = Form.useForm();
  const { Option } = Select;
  const [centerList, setCenterList] = useState([]);
  const [checkByUserList, setCheckByUserList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pdfFile, setPdfFile] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [subCategory, setSubCategory] = useState([]);

  const fetchCategoryList = () => {
    axios
      .get(
        `${config.baseUrl}/budget/expense_category_list/?pagination=False`,
        {
          headers: authHeader(),
        },
      )
      .then((response) => {
        const resData = Array.isArray(response?.data)
          ? response?.data
          : [];
        setCategoryList(resData);
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          title: "Error",
          text: error,
          confirmButtonText: "Ok",
          icon: "error",
        });
      });
  };

  const fetchDocument = async (document) => {
    try {
      const response = await axios.get(`${config.baseUrl}/api/s3-file-url/`, {
        headers: authHeader(),
        params: {
          file_key: document,
          file_type: "document", // or "document" depending on your use case
        },
      });

      // Assuming the response contains a URL in the data
      const fileUrl = response?.data?.imgUrl;

      if (fileUrl) {
        setPdfFile([
          {
            uid: "-1",
            name: "expense_pdf",
            status: "done",
            url: fileUrl,
          },
        ]);
      }

      console.log(response);
    } catch (error) {
      console.error("Error fetching document:", error);
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || "Failed to fetch document",
        confirmButtonText: "Ok",
        icon: "error",
      });
    }
  };

  useEffect(() => {
    if (userProfile && !isUpdate) {
      form.setFieldsValue({
        prepared_by_user_id: userProfile?.employee_id,
      });
    }
  }, [userProfile, form, isUpdate]);

  useEffect(() => {
    if (data) {
      fetchDocument(data?.expense_pdf);
      form.setFieldsValue({
        prepared_by_user_id: data?.prepared_by?.prepared_by_id,
        center_id: data?.center?.center_id,
        total_expense: data?.total_expense_amount,
        checked_by: data?.checked_by?.checked_by_id,
        status: data?.status,
        remarks: data?.remarks,
        expense_pdf: data?.expense_pdf,
        expenses: data?.expense_details?.map((expense) => ({
          expense_desc: expense.expense_desc,
          expense_amount: expense.expense_amount,
          category: expense.category ? expense.category.id : null,
          sub_category: expense.sub_category ? expense.sub_category.id : null,
        })),
      });

      // Set subcategories for the first expense
      if (data?.expense_details?.length > 0) {
        const category = categoryList?.find(
          (item) => item.id === data?.expense_details[0]?.category?.id,
        );
        setSubCategory(category?.sub_categories || []);
      }
    }
  }, [data, form, categoryList]);

  useEffect(() => {
    axios
      .get(`${config.baseUrl}/inventory/center_list/`, {
        headers: authHeader(),
      })
      .then((response) => {
        setCenterList(response?.data);
      })
      .catch((error) => {
        Swal.fire({
          title: "Error",
          text: error,
          confirmButtonText: "Ok",
          icon: "error",
        });
      });
    fetchCategoryList();
  }, []);

  useEffect(() => {
    axios
      .get(`${config.baseUrl}/employee/sales_operations_users/`, {
        headers: authHeader(),
      })
      .then((response) => {
        setCheckByUserList(response?.data);
      })
      .catch((error) => {
        Swal.fire({
          title: "Error",
          text: error,
          confirmButtonText: "Ok",
          icon: "error",
        });
      });
  }, []);

  const calculateTotalExpense = () => {
    const formValues = form.getFieldsValue();
    const expenses = formValues.expenses || [];
    const newTotalExpense = expenses?.reduce((sum, expense) => {
      return sum + (parseFloat(expense?.expense_amount) || 0);
    }, 0);
    form.setFieldsValue({
      total_expense: newTotalExpense.toFixed(2),
    });
  };

  const handleModalClose = () => {
    form.setFieldsValue({
      prepared_by_user_id: null,
      center_id: null,
      total_expense: null,
      checked_by: null,
      status: null,
      remarks: null,
      expenses: [],
    });
    modalClose();
  };

  const handleFileChange = useCallback(async ({ fileList }) => {
    if (fileList.length > 1) {
      Swal.fire({
        title: "Error",
        text: "Can't upload more than 1 file",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }
    setPdfFile(fileList);

    if (fileList && fileList[0]) {
      const file = fileList[0].originFileObj;

      try {
        const formData = new FormData();
        formData.append("file_name", file.name);
        formData.append("mime_type", file.type);
        formData.append("file_type", "document");
        formData.append("file_uid", file.uid);

        const response = await axios.post(
          `${config.baseUrl}/api/upload-file/`,
          formData,
          {
            headers: authHeader(),
          },
        );

        const { signedUrl } = response.data;

        const s3Response = await axios.put(signedUrl, file, {
          headers: {
            "Content-Type": file.type,
          },
        });

        if (s3Response.status === 200) {
          Swal.fire({
            title: "Success",
            text: "PDF Uploaded Successfully",
            icon: "success",
            confirmButtonText: "OK",
          });
        } else {
          throw new Error("Failed to upload to S3");
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.message || "Upload failed";
        setPdfFile([]);
        Swal.fire({
          title: "Error",
          text: errorMessage,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    }
  }, []);

  const handleExpenseCreate = (value) => {
    setLoading(true);
    if (pdfFile[0]?.originFileObj) {
      const file = pdfFile[0].originFileObj;

      value.expense_pdf = file?.uid + "_" + file?.name;
    }
    axios
      .post(`${config.baseUrl}/budget/create_expense/`, value, {
        headers: authHeader(),
      })
      .then((response) => {
        setLoading(false);
        if (response?.status === 201) {
          Swal.fire({
            title: "Success",
            text: response?.data?.message,
            confirmButtonText: "Ok",
            icon: "success",
          }).then(() => {
            form.resetFields();
            modalClose();
            fetchExpenseList();
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        Swal.fire({
          title: "Error",
          text: error,
          confirmButtonText: "Ok",
          icon: "error",
        });
      });
  };

  const handleExpenseUpdate = (value) => {
    value.total_expense = parseFloat(value.total_expense);
    if (pdfFile[0]?.originFileObj) {
      const file = pdfFile[0].originFileObj;

      value.expense_pdf = file?.uid + "_" + file?.name;
    }
    setLoading(true);
    axios
      .patch(
        `${config.baseUrl}/budget/update_expense/${data?.expense_id}/`,
        value,
        {
          headers: authHeader(),
        },
      )
      .then((response) => {
        setLoading(false);
        if (response?.status === 200) {
          Swal.fire({
            title: "Success",
            text: "Expense Updated Successful",
            confirmButtonText: "Ok",
            icon: "success",
          }).then(() => {
            form.resetFields();
            modalClose();
            fetchExpenseList();
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        Swal.fire({
          title: "Error",
          text: error,
          confirmButtonText: "Ok",
          icon: "error",
        });
      });
  };

  const handleCategoryChange = (value, expenseIndex) => {
    const selectedCategory = categoryList?.find((item) => item.id === value);
    setSubCategory(selectedCategory?.sub_categories || []);
    const expenses = form.getFieldValue("expenses");
    expenses[expenseIndex].sub_category = null; // Reset sub_category on category change
    form.setFieldsValue({ expenses });
  };

  return (
    <Modal
      title={isUpdate ? "Update Expense" : "Add Expense"}
      open={visible}
      onCancel={handleModalClose}
      footer={false}
      width="35%"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={isUpdate ? handleExpenseUpdate : handleExpenseCreate}
      >
        <Form.Item label="Prepared By">
          <Input
            readOnly
            defaultValue={
              isUpdate
                ? data?.prepared_by?.prepared_by_name
                : userProfile?.first_name + " " + userProfile?.last_name
            }
          />
        </Form.Item>

        <Form.Item name="prepared_by_user_id" hidden>
          <Input
            value={
              isUpdate
                ? userProfile?.employee_id
                : data?.prepared_by?.prepared_by_id
            }
            readOnly
          />
        </Form.Item>

        <Form.Item
          label="Center"
          name="center_id"
          rules={[{ required: true, message: "Center required!" }]}
        >
          <Select
            placeholder="Select center"
            allowClear
            showSearch
            disabled={
              isUpdate
                ? userProfile?.employee_id !== data?.prepared_by?.prepared_by_id
                : false
            }
          >
            {centerList?.map((center) => (
              <Option key={center?.id} value={center?.center?.employee_id}>
                {center?.center_name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.List name="expenses" initialValue={[{}]}>
          {(fields, { add, remove }) => (
            <>
              <Col lg={4}>
                <Form.Item>
                  <Button
                    size="small"
                    type="default"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    style={{ color: "#000000" }}
                    disabled={
                      isUpdate
                        ? userProfile?.employee_id !==
                          data?.prepared_by?.prepared_by_id
                        : false
                    }
                  >
                    Expense
                  </Button>
                </Form.Item>
              </Col>
              {fields.map(({ key, name, ...restField }, index) => (
                <Space key={index} align="baseline">
                  <Row className="border py-2 px-1" gutter={[8, 16, 24, 32]}>
                    <Col style={{ width: "200px" }}>
                      <Form.Item
                        {...restField}
                        name={[name, `category`]}
                        rules={[
                          {
                            required: true,
                            message: "Expense category required!",
                          },
                        ]}
                      >
                        <Select
                          placeholder="Expense Category"
                          disabled={
                            isUpdate
                              ? userProfile?.employee_id !==
                                data?.prepared_by?.prepared_by_id
                              : false
                          }
                          onChange={(value) =>
                            handleCategoryChange(value, index)
                          }
                        >
                          {categoryList?.map((category) => (
                            <Option key={category.id} value={category.id}>
                              {category.category_name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col style={{ width: "200px" }}>
                      <Form.Item {...restField} name={[name, `sub_category`]}>
                        <Select
                          placeholder="Expense Sub-Category"
                          disabled={
                            isUpdate
                              ? userProfile?.employee_id !==
                                data?.prepared_by?.prepared_by_id
                              : false
                          }
                        >
                          {subCategory?.map((sub) => (
                            <Option key={sub.id} value={sub.id}>
                              {sub.sub_category_name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col style={{ width: "200px" }}>
                      <Form.Item
                        {...restField}
                        name={[name, `expense_amount`]}
                        rules={[
                          { required: true, message: "Expense required!" },
                          {
                            pattern: /^[0-9]+(\.[0-9]+)?$/,
                            message: "Only english numbers",
                          },
                        ]}
                      >
                        <Input
                          placeholder="Expense Amount"
                          inputMode="numeric"
                          onChange={calculateTotalExpense}
                          disabled={
                            isUpdate
                              ? userProfile?.employee_id !==
                                data?.prepared_by?.prepared_by_id
                              : false
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col style={{ width: "550px" }}>
                      <Form.Item
                        {...restField}
                        name={[name, `expense_desc`]}
                        rules={[
                          {
                            required: true,
                            message: "Expense details required!",
                          },
                        ]}
                      >
                        <TextArea
                          placeholder="Details"
                          disabled={
                            isUpdate
                              ? userProfile?.employee_id !==
                                data?.prepared_by?.prepared_by_id
                              : false
                          }
                        />
                      </Form.Item>
                    </Col>

                    <Col className="d-flex align-items-start">
                      {fields.length > 1 && (
                        <Button
                          className="pt-0 mt-0"
                          size="small"
                          disabled={
                            isUpdate
                              ? userProfile?.employee_id !==
                                data?.prepared_by?.prepared_by_id
                              : false
                          }
                          onClick={() => {
                            form.setFieldsValue({
                              total_expense: undefined,
                            });
                            remove(name);
                            calculateTotalExpense();
                          }}
                          style={{
                            backgroundColor: "",
                            color: "darkred",
                            borderColor: "darkred",
                          }}
                        >
                          x
                        </Button>
                      )}
                    </Col>
                  </Row>
                </Space>
              ))}
            </>
          )}
        </Form.List>

        <div className="d-flex justify-content-end me-5 mt-2">
          <Row gutter={[8, 16, 24, 32]}>
            <Col>
              <Form.Item className="ms-2">
                <Input
                  className="text-center custom-placeholder-color"
                  placeholder="Total Expense"
                  disabled
                  style={{ color: "#46604A", borderColor: "#46604A" }}
                />
              </Form.Item>
            </Col>
            <Col style={{ width: "200px" }}>
              <Form.Item className="ms-1" name="total_expense">
                <Input
                  className="text-center"
                  placeholder="Total Expense"
                  readOnly
                  style={{ color: "#46604A", borderColor: "#46604A" }}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Form.Item
          label="Checked By"
          name="checked_by"
          rules={[{ required: true, message: "Required!" }]}
        >
          <Select
            placeholder="Select Checked By"
            showSearch
            allowClear
            optionFilterProp="children"
            disabled={
              isUpdate
                ? userProfile?.employee_id !== data?.prepared_by?.prepared_by_id
                : false
            }
          >
            {checkByUserList?.map((user) => (
              <Option key={user?.employee_id} value={user?.employee_id}>
                {user?.user_info?.first_name + " " + user?.user_info?.last_name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {isUpdate &&
          userProfile?.employee_id === data?.checked_by?.checked_by_id && (
            <Form.Item
              label="Status"
              name="status"
              rules={[{ required: true, message: "Status required!" }]}
            >
              <Select placeholder="Select Status">
                <Option value="Checked">Checked</Option>
              </Select>
            </Form.Item>
          )}

        <Form.Item label="Remarks" name="remarks">
          <Input placeholder="Remarks" />
        </Form.Item>

        <Form.Item
          name="expense_pdf"
          hasFeedback
          rules={[{ required: true, message: "Please upload expense pdf" }]}
        >
          <Upload
            onChange={handleFileChange}
            fileList={pdfFile}
            beforeUpload={() => false}
            listType="picture-card"
          >
            <button style={{ border: 0, background: "none" }} type="button">
              <UploadOutlined />
              <div style={{ marginTop: 1 }}>Expense PDF</div>
            </button>
          </Upload>
        </Form.Item>

        <Form.Item>
          <Button
            style={{
              backgroundColor: "#46604A",
              borderColor: "#46604A",
              color: "white",
            }}
            htmlType="submit"
          >
            {isUpdate ? "Update" : "Submit"}
            {loading && <Spin size="small" style={{ marginLeft: 8 }} />}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
}
